
import React, { useState } from 'react';
import { AlertTriangle, Trophy } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import SkillAssessmentDialog from '@/components/SkillAssessmentDialog';
import { AssessmentResult } from '@/types/skillAssessment';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/components/ui/sonner';

export const MAX_ASSESSMENT_SKIPS = 5;

interface SkillRatingPromptProps {
  onDismiss: () => void;
}

const SkillRatingPrompt: React.FC<SkillRatingPromptProps> = ({ onDismiss }) => {
  const { user, profile, refreshProfile } = useAuth();
  const [showSkillAssessment, setShowSkillAssessment] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isSkipping, setIsSkipping] = useState(false);

  const skipsUsed = profile?.skill_assessment_skips ?? 0;
  const skipsLeft = Math.max(0, MAX_ASSESSMENT_SKIPS - skipsUsed);
  const canSkip = skipsLeft > 0;

  const handleTakeAssessment = () => {
    setShowSkillAssessment(true);
  };

  const handleSkillAssessmentComplete = async (result: AssessmentResult) => {
    try {
      setIsSaving(true);
      
      if (!user?.id) {
        throw new Error('User not authenticated');
      }

      // Update profile with assessment results
      const { error } = await supabase
        .from('profiles')
        .update({
          skill_rating: result.eloRating,
          skill_rating_defined: true,
          skill_assessment_data: result as any, // Cast to any to satisfy Json type
          skill_assessment_date: new Date().toISOString(),
          skill_assessment_retakes: (profile?.skill_assessment_retakes || 0) + 1,
          skill_assessment_skips: 0,
          last_assessment_retake: new Date().toISOString(),
          needs_skill_validation: result.needsValidation,
          validation_matches_completed: result.needsValidation ? 0 : undefined
        })
        .eq('id', user.id);

      if (error) {
        throw error;
      }

      // Refresh the profile data to ensure the context has the latest information
      await refreshProfile();

      setShowSkillAssessment(false);
      onDismiss();
      
      toast.success(`Skill assessment completed! Your rating: ${result.eloRating.toFixed(2)}`);
      
      if (result.needsValidation) {
        toast.info('Your first 5 matches will validate this rating', {
          duration: 5000
        });
      }
    } catch (error) {
      console.error('Error saving assessment:', error);
      toast.error('Failed to save skill assessment');
    } finally {
      setIsSaving(false);
    }
  };


  const handleSkip = async () => {
    if (!canSkip) return;

    try {
      setIsSkipping(true);

      if (user?.id) {
        const { error } = await supabase
          .from('profiles')
          .update({ skill_assessment_skips: skipsUsed + 1 })
          .eq('id', user.id);

        if (error) throw error;
        await refreshProfile();
      }

      const remaining = skipsLeft - 1;
      toast.info(
        remaining > 0
          ? `Assessment skipped. You can skip ${remaining} more time${remaining === 1 ? '' : 's'}.`
          : 'That was your last skip. The assessment is required next time.'
      );
      onDismiss();
    } catch (error) {
      console.error('Error skipping assessment:', error);
      toast.error('Failed to skip the assessment');
    } finally {
      setIsSkipping(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
        <Card className="w-full max-w-md border-2 border-primary/20">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Trophy className="h-5 w-5 text-primary" />
              <CardTitle>{canSkip ? 'Complete Your Skill Assessment' : 'Skill Assessment Required'}</CardTitle>
            </div>
            <CardDescription>
              {canSkip
                ? 'Take our comprehensive skill assessment to get an accurate rating and better match recommendations.'
                : 'To ensure fair and balanced matches, all players must complete the skill assessment before using the platform.'
              }
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert className={canSkip ? "" : "border-primary/20 bg-primary/5"}>
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                {canSkip
                  ? 'Our advanced assessment evaluates your experience, technical skills, game knowledge, and competitive performance to provide a precise skill rating.'
                  : 'This assessment is mandatory and helps create balanced teams and fair competition for all players.'
                }
              </AlertDescription>
            </Alert>
            
            <div className="space-y-2 text-sm text-muted-foreground">
              <p>• 11 questions across 4 skill areas</p>
              <p>• Takes about 5 minutes to complete</p>
              <p>• More accurate than simple self-rating</p>
              <p>• Can be retaken monthly</p>
              {!canSkip && <p className="text-primary font-medium">• Required to access all features</p>}
            </div>
            
            <div className="flex flex-col gap-2">
              <Button 
                onClick={handleTakeAssessment} 
                className="w-full"
                disabled={isSaving}
              >
                <Trophy className="mr-2 h-4 w-4" />
                {canSkip ? 'Take Skill Assessment' : 'Complete Required Assessment'}
              </Button>
              {canSkip && (
                <Button
                  variant="outline"
                  onClick={handleSkip}
                  className="w-full min-h-[44px]"
                  disabled={isSaving || isSkipping}
                >
                  {isSkipping ? 'Skipping...' : 'Skip for now'}
                </Button>
              )}
              <p className="text-xs text-center text-muted-foreground">
                {canSkip
                  ? `You can skip this ${skipsLeft} more time${skipsLeft === 1 ? '' : 's'} (${skipsUsed}/${MAX_ASSESSMENT_SKIPS} skips used)`
                  : `You have used all ${MAX_ASSESSMENT_SKIPS} skips — completing the assessment is now required to continue.`}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      <SkillAssessmentDialog
        isOpen={showSkillAssessment}
        onOpenChange={setShowSkillAssessment}
        onComplete={handleSkillAssessmentComplete}
      />
    </>
  );
};

export default SkillRatingPrompt;
